import axios from "axios";
import { MiaPlaza } from "../Reinforced.Typings";
import { okCancel, prompt } from "./../Control/Vue/Notifications/Prompts";
import { postUrlEncoded } from "./LegacyAxiosUtil";

export class NewspaperEditor {
	private readonly setPriceOverlay: JQuery;
	private readonly categorySelect: JQuery;
	private readonly saveDraftButton: JQuery;
	private readonly submitButton: JQuery;
	private readonly newspaperExampleArticleTooltip: JQuery;
	private readonly submitAlwaysDisabled: boolean;
	private exampleArticles!: MiaPlaza.Control.Newspaper.IExampleArticle[];
	private readonly categorySelector: string = "#newspaperCreatorCategorySelector";
	private selectedExampleArrticle!: MiaPlaza.Control.Newspaper.IExampleArticle | null;
	private loading = false;

	constructor(containerId: DOMElementIdentifier, private constructionParameters: ConstructionParameters) {
		const container = jQuery("#" + containerId);
		this.setPriceOverlay = container.find("#setPriceOverlay");
		void axios.get<MiaPlaza.Control.Newspaper.IExampleArticle[]>(container.data("example-articles"))
			.then((response) => {
				this.exampleArticles = response.data;
			});
		this.saveDraftButton = container.find("#newspaperSaveDraftButton");
		this.saveDraftButton.on("click", (event) => void this.onSaveDraftButtonClick(event));

		const deleteDraftButton = container.find("#newspaperDeleteDraftButton");
		deleteDraftButton.on("click", (event) => void this.onDeleteDraftButtonClick(event));

		this.submitButton = container.find("#newspaperSubmitArticleButton");
		this.submitButton.on("click", this.onSubmitClick.bind(this));

		this.categorySelect = jQuery(this.categorySelector);
		this.categorySelect.on("change", this.onCategorySelectChange.bind(this));

		this.newspaperExampleArticleTooltip = jQuery("#newspaperExampleArticleTooltip");
		this.submitAlwaysDisabled = this.submitButton.attr("data-error-not-today") !== undefined;
		this.updateButtons();
		this.updateTooltip();
	}

	private updateButtons() {
		const setStatus = (el: JQuery, status: boolean, changeTooltip: boolean) => {
			el[status ? "removeClass" : "addClass"]("disabled");
			if (changeTooltip) {
				el.prop("title", status ? "" : el.data("error-tooltip") || "ERR_TOOLTIP_UNDEFINED");
			}
		};

		const validCat = this.categorySelect.val() !== "npCreatorCategoryUnselected";
		setStatus(this.submitButton, validCat && !this.submitAlwaysDisabled, !this.submitAlwaysDisabled);
		setStatus(this.saveDraftButton, validCat, true);
	}

	private updateTooltip() {
		if (this.selectedExampleArrticle) {
			this.newspaperExampleArticleTooltip.html(this.selectedExampleArrticle.Category);
		}
		else {
			this.newspaperExampleArticleTooltip.html("some_default_key_placeholder");
		}
	}

	private onCategorySelectChange(event: JQueryEventObject) {
		event.preventDefault();

		this.selectedExampleArrticle = this.getCategoryExampleById(Number(this.categorySelect.val()));
		if (this.selectedExampleArrticle) {
			jQuery(".mia-showExampleArticleLink").show();
			jQuery(".mia-articleExample").find(".modal-header h3").html(this.selectedExampleArrticle.Category);
			jQuery(".mia-articleExample").find(".modal-body").html("<h3>" + this.selectedExampleArrticle.Title + "</h3>"
				+ "<p>" + this.selectedExampleArrticle.Body + "</p>");
		}
		else {
			jQuery(".mia-showExampleArticleLink").hide();
		}
		this.updateButtons();
		this.updateTooltip();
	}

	private async onSaveDraftButtonClick(event: any): Promise<void> {
		event.preventDefault();
		if (event.currentTarget.classList.contains("disabled")) {
			const target = jQuery(event.currentTarget);
			prompt(target.data("error-tooltip") || "ERR_TOOLTIP_UNDEFINED");
			return;
		}

		if (this.constructionParameters.isNewArticle) {
			await this.saveDraft();
		}
		else {
			const isConfirmed = await okCancel(this.constructionParameters.saveDraftConfirmationText);
			if (isConfirmed) {
				await this.saveDraft();
			}
		}
	}

	private async saveDraft(): Promise<void> {
		if (this.loading) {
			return;
		}

		this.loading = true;
		const saveDraftData = {
			body: jQuery("#newspaperCreatorBodyTextBox").val(),
			categoryId: jQuery(this.categorySelector).val(),
			itemId: jQuery("#attachedItemSelector").val(),
			title: jQuery("#newspaperCreatorTitleTextBox").val(),
		};

		try {
			const response = await postUrlEncoded(this.constructionParameters.saveDraftUrl, saveDraftData);
			this.onSaveDraftSucceeded(response.data);
		}
		finally {
			this.loading = false;
		}
	}

	private onSaveDraftSucceeded(data: any): void {
		if (data.success) {
			this.windowRedirectTo(data.redirectUrl);
		}
		else {
			this.showErrorMessage(data.errorMessage);
		}
	}

	private windowRedirectTo(location: string): void {
		if (location) {
			window.location.href = location;
		}
		else {
			window.location.reload();
		}
	}

	private showErrorMessage(errorMessage: string): void {
		if (errorMessage) {
			prompt(errorMessage);
		}
	}

	private async onDeleteDraftButtonClick(event: JQuery.Event): Promise<void> {
		event.preventDefault();

		const isConfirmed = await okCancel(this.constructionParameters.deleteDraftConfirmationText);
		if (isConfirmed) {
			await this.deleteDraft();
		}
	}

	private async deleteDraft(): Promise<void> {
		if (this.loading) {
			return;
		}

		this.loading = true;
		try {
			const { data } = await axios.get(this.constructionParameters.deleteDraftUrl);
			this.onDeleteDraftSucceeded(data);
		}
		finally {
			this.loading = false;
		}
	}

	private onDeleteDraftSucceeded(data: any): void {
		this.windowRedirectTo(data.redirectUrl);
	}

	private onSubmitClick(event: any): void {
		event.preventDefault();
		if (event.currentTarget.classList.contains("disabled")) {
			const target = jQuery(event.currentTarget);
			prompt(target.data("error-not-today")
				|| target.data("error-tooltip")
				|| "ERR_TOOLTIP_UNDEFINED");
			return;
		}

		this.showSetPriceOverlay();

		const overlaySubmitButton = this.setPriceOverlay.find("#newspaperOverlayButtonSubmit");
		overlaySubmitButton.on("click", (clickEvent) => void this.onOverlaySubmitClick(clickEvent));

		const overlayCancelButton = this.setPriceOverlay.find("#newspaperOverlayButtonCancel");
		overlayCancelButton.on("click", this.onOverlayCancelClick.bind(this));
	}

	private showSetPriceOverlay(): void {
		this.setPriceOverlay.removeClass("hidden");
	}

	private hideSetPriceOverlay(): void {
		this.setPriceOverlay.addClass("hidden");
	}

	private async onOverlaySubmitClick(event: JQuery.Event): Promise<void> {
		event.preventDefault();

		if (this.loading) {
			return;
		}

		this.loading = true;
		const submitData = {
			body: jQuery("#newspaperCreatorBodyTextBox").val(),
			categoryId: jQuery("#newspaperCreatorCategorySelector").val(),
			itemId: jQuery("#attachedItemSelector").val(),
			price: this.setPriceOverlay.find("#newspaperOverlayPriceInput").val(),
			title: jQuery("#newspaperCreatorTitleTextBox").val(),
		};

		try {
			const response = await postUrlEncoded(this.constructionParameters.submitArticleUrl, submitData);
			this.onSubmitArticleSucceeded(response.data);
		}
		finally {
			this.loading = false;
		}
	}

	private onSubmitArticleSucceeded(data: any): void {
		if (data.success) {
			this.windowRedirectTo(data.redirectUrl);
		}
		else {
			if (data.hideOverlay) {
				this.hideSetPriceOverlay();
			}
			this.showErrorMessage(data.errorMessage);
		}
	}

	private onOverlayCancelClick(event: JQuery.Event): void {
		event.preventDefault();

		this.hideSetPriceOverlay();
	}

	private getCategoryExampleById(categoryId: number): MiaPlaza.Control.Newspaper.IExampleArticle | null {
		for (const elem of this.exampleArticles) {
			if (elem.CategoryId === categoryId) {
				return elem;
			}
		}
		return null;
	}
}
